import React from 'react';
import { Box, IconButton, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Link } from 'react-router-dom'; // Import Link from React Router

import ColorModeSelect from '@/components/ui/button/ColorModeSelect';
import DrawerWrapper from './DrawerWrapper';
import { useAuth } from '@/lib/firebase/auth'; // Use useAuth to access logout

const RightDrawer = ({ open, toggleDrawer, logoutHandler }) => {
  const { user, userDetails, logout } = useAuth(); // Access userDetails for role check

  // Determine if the user is an admin
  const isAdminOrStaff = userDetails?.appRole === 'admin' || userDetails?.appRole === 'staff';

  const rightMenuSections = [
    {
      // Settings section
      component: <ColorModeSelect />, // Use the ColorModeSelect here
    },
    {
      // Section for links and actions
      items: [
        {
          label: "Settings",
          icon: <SettingsIcon />,
          link: "/settings",
        },
        ...(isAdminOrStaff
          ? [{
            label: "EPL Admin",
            icon: <AdminPanelSettingsIcon />,
            link: "/epl-admin",
          }]
          : []), // Conditionally include EPL Admin for admins only
        {
          label: "Logout",
          icon: <ExitToAppIcon />,
          action: logout, // Call the logout function from Firebase
        },
        // {
        //   label: "Logout",
        //   icon: <ExitToAppIcon />,
        //   action: logoutHandler, // Use the existing logoutHandler passed as a prop
        // },
      ],
    },
  ];

  return (
    <DrawerWrapper
      anchor="right"
      open={open}
      toggleDrawer={toggleDrawer}
      drawerContent={
        <>
          {/* User Information Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
            <Link to="/settings/account" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Avatar for Profile Picture */}
                {userDetails?.profilePicture ? (
                  <Avatar alt="Profile Picture" src={userDetails.profilePicture} sx={{ width: 32, height: 32, mr: 1 }} />
                ) : (
                  <AccountCircleIcon sx={{ fontSize: 34, mr: 1 }} />
                )}
                <Typography variant="body2" sx={{ color: 'text.primary', fontSize: 14, textTransform: 'lowercase' }}>
                  {user?.displayName || user?.email || 'No user account.'}
                </Typography>
              </Box>
            </Link>
            <IconButton onClick={toggleDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Render sections */}
          {rightMenuSections.map((section, index) => (
            <Box key={index}>
              {/* If the section has a component like ColorModeSelect, render it */}
              {section.component ? (
                <Box sx={{ p: 2 }}>{section.component}</Box>
              ) : (
                <List>
                  {/* Render the items */}
                  {section.items.map((item, itemIndex) => (
                    <ListItem key={itemIndex} disablePadding>
                      <ListItemButton
                        component={item.link ? Link : 'button'}
                        to={item.link || ''}
                        onClick={item.action ? item.action : toggleDrawer} // Handle action or close drawer
                        sx={{
                          height: 32,
                          padding: '8px',
                          borderRadius: '4px',
                          justifyContent: open ? 'initial' : 'center',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                            color: 'text.primary',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {open && (
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  fontWeight: 'normal',
                                  fontFamily: 'Monospace, sans-serif', // Ensures lower case, matching other drawer items
                                  textTransform: 'lowercase',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {item.label}
                              </Typography>
                            }
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          ))}
        </>
      }
    />
  );
};

export default RightDrawer;
